import React, { useMemo, useState } from 'react';
import {
  Bell,
  Briefcase,
  CheckCheck,
  CreditCard,
  FileCheck,
  FileStack,
  FileText,
  MessageSquare,
  Package,
  Receipt,
  UserPlus,
} from 'lucide-react';
import Pagination, { usePagination } from './Pagination';

const KIND_ICON = {
  order: Package,
  payment: CreditCard,
  message: MessageSquare,
  mission: Briefcase,
  review: FileCheck,
  quote: FileText,
  invoice: Receipt,
  user: UserPlus,
  document: FileStack,
};

function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso);
  const diff = Date.now() - then.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "À l'instant";
  if (mins < 60) return `Il y a ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Hier';
  if (days < 7) return `Il y a ${days} j`;
  return then.toLocaleDateString();
}

const KIND_ORDER = ['message', 'order', 'payment', 'mission', 'review', 'quote', 'invoice', 'document', 'user', 'info'];

const KIND_LABEL = {
  message: 'Messages',
  order: 'Commandes',
  payment: 'Paiements',
  mission: 'Missions',
  review: 'Vérifications',
  quote: 'Devis',
  invoice: 'Factures',
  document: 'Documents',
  user: 'Utilisateurs',
  info: 'Autres',
};

export default function NotificationsPanel({ items = [], onOpen, onMarkAll, labels = {} }) {
  const [tab, setTab] = useState('all');
  const unreadCount = items.filter((n) => !n.readAt).length;
  const filtered = useMemo(
    () => (tab === 'unread' ? items.filter((n) => !n.readAt) : items),
    [items, tab],
  );
  const groups = useMemo(() => {
    const byKind = new Map();
    filtered.forEach((n) => {
      const kind = n.kind || 'info';
      if (!byKind.has(kind)) byKind.set(kind, []);
      byKind.get(kind).push(n);
    });
    return KIND_ORDER.filter((k) => byKind.has(k)).map((k) => ({
      kind: k,
      items: byKind.get(k),
    }));
  }, [filtered]);
  const pg = usePagination(groups, 6);

  return (
    <section className="dash-panel notif-panel">
      <div className="notif-toolbar">
        <div className="notif-tabs">
          <button
            type="button"
            className={`notif-tab${tab === 'all' ? ' is-active' : ''}`}
            onClick={() => setTab('all')}
          >
            {labels.all || 'Toutes'}
          </button>
          <button
            type="button"
            className={`notif-tab${tab === 'unread' ? ' is-active' : ''}`}
            onClick={() => setTab('unread')}
          >
            {labels.unread || 'Non lues'}
            {unreadCount > 0 && <span className="notif-tab-count">{unreadCount}</span>}
          </button>
        </div>
        {unreadCount > 0 && (
          <button type="button" className="notif-markall" onClick={onMarkAll}>
            <CheckCheck size={15} />
            {labels.markAllRead || 'Tout marquer comme lu'}
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <p className="dash-empty">{labels.empty || 'Aucune notification.'}</p>
      ) : (
        pg.pageItems.map((g) => {
          const GroupIcon = KIND_ICON[g.kind] || Bell;
          const groupUnread = g.items.filter((n) => !n.readAt).length;
          return (
            <div key={g.kind} className="notif-group">
              <p className="notif-group-title">
                <GroupIcon size={14} />
                {KIND_LABEL[g.kind] || g.kind}
                {groupUnread > 0 && <span className="notif-tab-count">{groupUnread}</span>}
              </p>
              <ul className="notif-list">
                {g.items.map((n) => {
                  const Icon = KIND_ICON[n.kind] || Bell;
                  const unread = !n.readAt;
                  return (
                    <li key={n.id}>
                      <button
                        type="button"
                        className={`notif-item${unread ? ' is-unread' : ''}`}
                        onClick={() => onOpen?.(n)}
                      >
                        <span className={`notif-icon notif-icon--${n.kind || 'info'}`}>
                          <Icon size={17} />
                        </span>
                        <span className="notif-item-meta">
                          <span className="notif-item-title">{n.title}</span>
                          {n.body && <span className="notif-item-body">{n.body}</span>}
                          <span className="notif-item-time">{relativeTime(n.createdAt)}</span>
                        </span>
                        {unread && <span className="notif-dot" aria-label="Non lue" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })
      )}
      <Pagination page={pg.page} totalPages={pg.totalPages} total={pg.total} onChange={pg.setPage} />
    </section>
  );
}
